/**
 * One-click Log time presets (#36): 15 / 30 / 60 minutes against a guessed
 * `billable_kind`. "Log time…" keeps the full form for a custom amount or source.
 */

import {
  isRef,
  taskMachine,
  type BillableKind,
  type ServiceCategory,
  type TaskView,
} from '@fareast/shared'

import { messageOf } from '../../../api/problem.js'
import { useStudioAction, useStudioRead } from '../../../api/studio.js'
import { Button, ButtonRow } from '../../../components/portal/Button.js'
import { Notice } from '../../../components/portal/Status.js'
import { Muted, Small } from '../../../components/portal/Text.js'
import { ActionDialog } from '../../../components/studio/ActionDialog.js'
import { KEY_TARGET } from './keyTargets.js'
import { billableKindForServiceCategory } from '../../../components/studio/queueForService.js'

export const LOG_TIME_PRESETS: readonly {
  readonly minutes: number
  readonly label: string
}[] = [
  { minutes: 15, label: '15 min' },
  { minutes: 30, label: '30 min' },
  { minutes: 60, label: '1 h' },
]

/**
 * What these minutes most likely draw on (#36 / #44).
 * Money ref wins; else the service category from the rate card; else client →
 * retainer; else the studio absorbs it.
 */
export function billableKindFor(
  task: TaskView,
  serviceCategory?: ServiceCategory | null,
): BillableKind {
  /*
   * An invoice reference is a billing chase (the failed-payment finance task), which
   * `tasks_log_time` itself calls `account` work — only a work order or an extra is
   * a `project`'s money.
   */
  if (task.money_ref !== null) return isRef('invoice', task.money_ref) ? 'account' : 'project'
  if (serviceCategory !== undefined && serviceCategory !== null) {
    return billableKindForServiceCategory(serviceCategory)
  }
  if (task.client !== null) return 'retainer'
  return 'account'
}

/**
 * True when what a task's minutes draw on is the **rate card's** to say: nothing on
 * the task settles it (`billableKindFor` — a money reference wins outright), and it
 * names a service whose category is the answer.
 */
export function asksTheRateCard(task: TaskView): boolean {
  return task.money_ref === null && task.service_code !== null
}

export type LogTimeControlProps = {
  readonly task: TaskView
  readonly disabled?: boolean | undefined
}

export function LogTimeControl({ task, disabled }: LogTimeControlProps) {
  const log = useStudioAction('tasks_log_time')
  const services = useStudioRead('catalog_list_services', {}, { inline: true })
  const asks = asksTheRateCard(task)
  const category = asks
    ? (services.data?.services.find((one) => one.code === task.service_code)?.category ?? null)
    : null
  /*
   * The kind, or **null while nothing here can say it**.
   *
   * A task that names a service is billed as that service's category says, and the
   * category is on the rate card — a read of its own, `inline`, that answers a moment
   * after the task does and sometimes not at all. `billableKindFor` with no category
   * falls through to "a client, so their retainer", which is the right answer for a
   * task that names no service and was the answer given for one whose service had
   * simply not been looked up yet: fifteen minutes of project work, pressed in the
   * first second or over a rate card that could not be read, were filed against the
   * client's package. `time_entry` is appended to and never edited, and the
   * effective rate the studio reads per client is drawn from it.
   *
   * So the one-click buttons wait for the fact they file. A service the rate card
   * answered without — one no longer sold is left out of the list — is the same
   * "cannot say": the full form asks, and the reader who knows is the one who answers.
   */
  const billable_kind = asks && category === null ? null : billableKindFor(task, category)
  const pending = disabled === true || log.isPending

  if (taskMachine.isTerminal(task.status)) return null

  return (
    <div>
      <ButtonRow>
        {LOG_TIME_PRESETS.map((preset) => (
          <Button
            key={preset.minutes}
            size="sm"
            disabled={pending || billable_kind === null}
            onClick={() => {
              if (billable_kind === null) return
              log.mutate({ task_ref: task.ref, minutes: preset.minutes, billable_kind })
            }}
          >
            {preset.label}
          </Button>
        ))}
        <ActionDialog
          action="tasks_log_time"
          label="Log time…"
          title="Log time against it"
          submitLabel="Log time"
          size="sm"
          fixed={{ task_ref: task.ref }}
          defaults={billable_kind === null ? undefined : { billable_kind }}
          trigger={
            <Button size="sm" id={KEY_TARGET.log}>
              Log time…
            </Button>
          }
        />
      </ButtonRow>
      {billable_kind === null && !services.isPending ? (
        <Small>
          <Muted>
            {services.data === undefined
              ? 'The rate card could not be read, so the quick buttons cannot tell what this time is billed to. Log time… asks.'
              : 'The rate card does not list this task’s service, so the quick buttons cannot tell what this time is billed to. Log time… asks.'}
          </Muted>
        </Small>
      ) : null}
      {log.isError ? (
        <Notice need role="alert">
          {messageOf(log.error)}
        </Notice>
      ) : null}
    </div>
  )
}
